import { DeleteOutlined } from '@ant-design/icons';
import {
  App,
  Badge,
  Button,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  type TableColumnsType,
  type TableProps,
} from 'antd';
import dayjs from 'dayjs';
import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  type MouseEvent,
} from 'react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_BARRIER_SORT } from '@/hooks/useBarriersLibrary';
import type {
  BarrierDirection,
  BarrierListItem,
  BarrierListSort,
  BarrierSortField,
} from '@/types/barriers';

const LAT_LON_DECIMALS = 5;
const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

const DIRECTION_COLORS: Record<BarrierDirection, string> = {
  both: 'default',
  forward: 'blue',
  backward: 'purple',
};

type AntSortOrder = 'ascend' | 'descend' | null;

const SORTABLE_COLUMN_KEYS: readonly BarrierSortField[] = [
  'radius_m',
  'created_at',
  'updated_at',
];

function isSortField(key: unknown): key is BarrierSortField {
  return (
    typeof key === 'string' &&
    (SORTABLE_COLUMN_KEYS as readonly string[]).includes(key)
  );
}

function sortOrderFor(
  field: BarrierSortField,
  sort: BarrierListSort,
): AntSortOrder {
  if (sort.field !== field) return null;
  return sort.direction === 'asc' ? 'ascend' : 'descend';
}

export interface BarriersLibraryTableHandle {
  /** Scrolls the row for `barrierId` into view, if present on the page. */
  readonly scrollToBarrier: (barrierId: number) => void;
}

interface BarriersLibraryTableProps {
  readonly items: readonly BarrierListItem[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly loading: boolean;
  readonly selectedRowKeys: readonly number[];
  readonly focusedBarrierId: number | null;
  readonly bulkBusy: boolean;
  readonly selectAllBusy: boolean;
  readonly sort: BarrierListSort;
  readonly onSortChange: (next: BarrierListSort) => void;
  readonly onPageChange: (page: number) => void;
  readonly onSelectionChange: (keys: readonly number[]) => void;
  readonly onSelectAllAcrossPages: () => void;
  readonly onClearSelection: () => void;
  readonly onRowClick: (barrier: BarrierListItem) => void;
  readonly onBulkDelete: () => void;
}

const BarriersLibraryTable = forwardRef<
  BarriersLibraryTableHandle,
  BarriersLibraryTableProps
>(function BarriersLibraryTable(props, ref) {
  const {
    items,
    total,
    page,
    pageSize,
    loading,
    selectedRowKeys,
    focusedBarrierId,
    bulkBusy,
    selectAllBusy,
    sort,
    onSortChange,
    onPageChange,
    onSelectionChange,
    onSelectAllAcrossPages,
    onClearSelection,
    onRowClick,
    onBulkDelete,
  } = props;
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const wrapperRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      scrollToBarrier: (barrierId: number) => {
        const node = wrapperRef.current?.querySelector<HTMLElement>(
          `[data-row-key="${barrierId}"]`,
        );
        node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
    }),
    [],
  );

  const columns = useMemo(() => _buildColumns(t, sort), [t, sort]);

  const handleTableChange: TableProps<BarrierListItem>['onChange'] = (
    _pagination,
    _filters,
    sorter,
    extra,
  ) => {
    if (extra.action !== 'sort') return;
    const single = Array.isArray(sorter) ? sorter[0] : sorter;
    if (!single?.order || !isSortField(single.columnKey)) {
      // Third click clears the column sorter — fall back to the default.
      onSortChange(DEFAULT_BARRIER_SORT);
      return;
    }
    onSortChange({
      field: single.columnKey,
      direction: single.order === 'ascend' ? 'asc' : 'desc',
    });
  };

  const rowSelection: TableProps<BarrierListItem>['rowSelection'] = {
    selectedRowKeys: [...selectedRowKeys],
    onChange: (keys) => onSelectionChange(keys.map(Number)),
    preserveSelectedRowKeys: true,
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_NONE,
      {
        key: 'all-pages',
        text: t('barriers.library.bulk.selectAllPages', { count: total }),
        onSelect: onSelectAllAcrossPages,
      },
    ],
    getCheckboxProps: () => ({
      onClick: (event: MouseEvent) => event.stopPropagation(),
    }),
  };

  const handleBulkDeleteClick = () => {
    modal.confirm({
      title: t('barriers.library.bulk.deleteConfirmTitle', {
        count: selectedRowKeys.length,
      }),
      content: t('barriers.library.bulk.deleteConfirmDescription'),
      okText: t('common.delete'),
      cancelText: t('common.cancel'),
      okButtonProps: { danger: true },
      onOk: onBulkDelete,
    });
  };

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <BulkActionBar
        selectedCount={selectedRowKeys.length}
        total={total}
        busy={bulkBusy}
        selectAllBusy={selectAllBusy}
        onDelete={handleBulkDeleteClick}
        onSelectAllAcrossPages={onSelectAllAcrossPages}
        onClearSelection={onClearSelection}
      />
      <div ref={wrapperRef}>
        <Table<BarrierListItem>
          size="small"
          rowKey="id"
          columns={columns}
          dataSource={items as BarrierListItem[]}
          loading={loading}
          rowSelection={rowSelection}
          onChange={handleTableChange}
          showSorterTooltip={false}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: false,
            size: 'small',
            onChange: onPageChange,
            showTotal: (totalCount, range) =>
              t('barriers.library.pagination.showTotal', {
                from: range[0],
                to: range[1],
                total: totalCount,
              }),
          }}
          onRow={(record) => ({
            onClick: () => onRowClick(record),
            style: {
              cursor: 'pointer',
              background:
                record.id === focusedBarrierId ? '#f6ffed' : undefined,
            },
          })}
          scroll={{ x: 'max-content' }}
        />
      </div>
    </Space>
  );
});

export default BarriersLibraryTable;

interface BulkActionBarProps {
  readonly selectedCount: number;
  readonly total: number;
  readonly busy: boolean;
  readonly selectAllBusy: boolean;
  readonly onDelete: () => void;
  readonly onSelectAllAcrossPages: () => void;
  readonly onClearSelection: () => void;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

function _buildColumns(
  t: Translate,
  sort: BarrierListSort,
): TableColumnsType<BarrierListItem> {
  return [
    {
      title: t('barriers.library.columns.id'),
      dataIndex: 'id',
      key: 'id',
      width: 70,
      render: (value: number) => (
        <Typography.Text code style={{ fontSize: 11 }}>
          {value}
        </Typography.Text>
      ),
    },
    {
      title: t('barriers.library.columns.name'),
      dataIndex: 'name',
      key: 'name',
      ellipsis: { showTitle: false },
      render: (value: string, row) => (
        <Tooltip title={row.comment ?? value} placement="topLeft">
          <Typography.Text>{value}</Typography.Text>
        </Tooltip>
      ),
    },
    {
      title: t('barriers.library.columns.coords'),
      key: 'coords',
      width: 160,
      render: (_value, row) => (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {row.point.lat.toFixed(LAT_LON_DECIMALS)},{' '}
          {row.point.lon.toFixed(LAT_LON_DECIMALS)}
        </Typography.Text>
      ),
    },
    {
      title: t('barriers.library.columns.radius'),
      dataIndex: 'radius_m',
      key: 'radius_m',
      width: 100,
      align: 'right',
      sorter: true,
      sortOrder: sortOrderFor('radius_m', sort),
      render: (value: number) => `${value} ${t('common.units.meters')}`,
    },
    {
      title: t('barriers.library.columns.direction'),
      dataIndex: 'direction',
      key: 'direction',
      width: 130,
      render: (value: BarrierDirection) => (
        <Tag color={DIRECTION_COLORS[value]} style={{ margin: 0 }}>
          {t(`barriers.direction.${value}`)}
        </Tag>
      ),
    },
    {
      title: t('barriers.library.columns.waypoints'),
      dataIndex: 'auto_waypoints_count',
      key: 'auto_waypoints_count',
      width: 110,
      align: 'center',
      render: (value: number) => (
        <Badge
          count={value}
          showZero
          color={value === 0 ? 'orange' : 'green'}
          overflowCount={99}
        />
      ),
    },
    {
      title: t('barriers.library.columns.created'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 165,
      sorter: true,
      sortOrder: sortOrderFor('created_at', sort),
      render: (value: string) => dayjs(value).format(DATE_TIME_FORMAT),
    },
    {
      title: t('barriers.library.columns.updated'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 165,
      sorter: true,
      sortOrder: sortOrderFor('updated_at', sort),
      render: (value: string) => dayjs(value).format(DATE_TIME_FORMAT),
    },
  ];
}

function BulkActionBar({
  selectedCount,
  total,
  busy,
  selectAllBusy,
  onDelete,
  onSelectAllAcrossPages,
  onClearSelection,
}: BulkActionBarProps) {
  const { t } = useTranslation();
  const disabled = selectedCount === 0 || busy;
  const canSelectAll = selectedCount < total && !busy;
  return (
    <Space size="small" align="center" wrap>
      <Typography.Text type="secondary">
        {t('barriers.library.bulk.selected', { count: selectedCount })}
      </Typography.Text>
      {canSelectAll && (
        <Button
          size="small"
          type="link"
          style={{ padding: 0 }}
          loading={selectAllBusy}
          onClick={onSelectAllAcrossPages}
        >
          {t('barriers.library.bulk.selectAllPages', { count: total })}
        </Button>
      )}
      {selectedCount > 0 && !busy && (
        <Button size="small" type="link" style={{ padding: 0 }} onClick={onClearSelection}>
          {t('barriers.library.bulk.clearSelection')}
        </Button>
      )}
      <Button
        size="small"
        danger
        icon={<DeleteOutlined />}
        onClick={onDelete}
        disabled={disabled}
        loading={busy}
      >
        {t('barriers.library.bulk.delete')}
      </Button>
    </Space>
  );
}
